import { inRange, partyBalance, partyLedger, partyStatus } from '@khata/core';
import { Empty, MonthPicker, Row, ShowMore } from '../components/index.js';
import { usePaged } from '../lib/paging.js';
import { dShort, num, qty, taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { entries, master, state, today } from '../lib/khata.js';
import { monthsWithEntries, periodRange } from '../lib/lists.js';
import { accountName, itemName, linesSummary, returnReason, typeLabel } from '../lib/labels.js';
import { go } from '../lib/router.js';
import { listPeriod, openSheet } from '../lib/ui.js';

export function Party(props: { id: string }) {
  const s = state.value;
  const m = master.value;
  const p = m.parties.get(props.id);
  if (!p) {
    return (
      <>
        <button type="button" class="btn btn-ghost" onClick={() => go('parties')}>
          {t('party_back')}
        </button>
        <Empty>{t('party_missing')}</Empty>
      </>
    );
  }
  const isC = p.type === 'customer';
  const bal = partyBalance(s, p);
  const status = partyStatus(bal);
  const range = periodRange(listPeriod.value, today.value);
  const ledger = partyLedger(s, m, entries.value, p.id)
    .filter((r) => inRange(r.entry.date, range))
    .reverse();
  const paged = usePaged(ledger, 100);

  return (
    <>
      <button
        type="button"
        class="btn btn-ghost"
        style={{ marginLeft: -5, marginBottom: 8 }}
        onClick={() => go('parties')}
      >
        {t('party_back')}
      </button>
      <header style={{ marginBottom: 20 }}>
        <div class="page-kicker">
          {isC ? t('customer') : t('vendor')}
          {p.phone ? ' · ' + p.phone : ''}
          {p.note ? ' · ' + p.note : ''}
          {p.active ? '' : ' · ' + t('archived')}
        </div>
        <h1 class="page-title" style={{ fontSize: 32, margin: '4px 0 6px' }}>
          {p.name}
        </h1>
        <div class="small muted">
          {status === 'due'
            ? isC
              ? t('party_owesYou')
              : t('party_youOwe')
            : status === 'advance'
              ? t('party_advanceHeld')
              : t('party_settled')}
        </div>
        <div
          class={'kpi-value ' + (status === 'due' ? (isC ? 'bad' : 'ink') : 'muted')}
          style={{ fontSize: 30 }}
        >
          {taka(bal)}
        </div>
      </header>
      <div class="actions" style={{ marginBottom: 30 }}>
        <button
          type="button"
          class="btn btn-primary"
          onClick={() =>
            openSheet({
              kind: 'payment',
              direction: isC ? 'in' : 'out',
              partyId: p.id,
              amount: bal > 0 ? bal : undefined,
            })
          }
        >
          {isC ? t('party_receive') : t('party_pay')}
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          onClick={() => openSheet({ kind: isC ? 'sale' : 'purchase', partyId: p.id })}
        >
          {isC ? t('party_newSale') : t('party_newPurchase')}
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          onClick={() => openSheet({ kind: 'party', id: p.id })}
        >
          {t('edit')}
        </button>
      </div>
      <h6 class="section">{t('party_ledger')}</h6>
      <MonthPicker months={monthsWithEntries(entries.value)} />
      <div class="list">
        {ledger.length === 0 && <Empty>{t('party_noEntries', { name: p.name })}</Empty>}
        {paged.visible.map((r) => {
          const e = r.entry;
          let title = '';
          let sub = dShort(e.date);
          if (e.type === 'sale' || e.type === 'purchase') {
            title = `${typeLabel(e.type)} #${num(e.no)}`;
            sub += ` · ${t('party_total')} ${taka(e.total)} · ${t('party_paid')} ${taka(e.paid)} · ${linesSummary(m, e)}`;
          } else if (e.type === 'payment') {
            title = isC ? t('party_paymentReceived') : t('party_paymentMade');
            sub += ` · ${accountName(m, e.accountId)}${e.note ? ' · ' + e.note : ''}`;
          } else if (e.type === 'return') {
            title = `${t('type_return')} · ${itemName(m, e.itemId)} ×${qty(e.qty)}`;
            sub += ` · ${returnReason(e.reason)}`;
          } else if (e.type === 'party_opening') {
            title = t('party_openingBalance');
          } else title = typeLabel(e.type);
          const amount = r.voided
            ? t('voided')
            : (r.delta > 0 ? '+' : '') +
              taka(r.delta) +
              (r.delta > 0 && (e.type === 'sale' || e.type === 'purchase') ? ` ${t('due')}` : '');
          return (
            <Row
              key={e.id}
              title={title}
              sub={sub}
              amount={amount}
              amountTone={r.voided ? 'muted' : r.delta > 0 ? 'bad' : r.delta < 0 ? 'good' : 'muted'}
              amountSub={`${t('party_bal')} ${taka(r.running)}`}
              strike={r.voided}
              onClick={() => openSheet({ kind: 'entry', id: e.id })}
            />
          );
        })}
      </div>
      <ShowMore shown={paged.shown} total={paged.total} onMore={paged.more} />
    </>
  );
}
